/**
 * Reads the `asm_template_guard_enabled` system_config flag so the CRM's client-side
 * dead-unsubscribe-link gates track the SAME switch as the edge functions (ASM flag-gate,
 * audit FG-B1).
 *
 * The dead-link template guard (a template using {{unsubscribe}} / {{unsubscribe_preferences}},
 * which render an EMPTY link on an API send) is enforced authoritatively in the send paths. The
 * three frontend gates (FlightForm, PreSendConfirmationModal, StepEditor) are a UX convenience
 * that blocks the operator early. They MUST follow the flag:
 *   - flag ON  → block (the operator can't save/send a dead-link template — same as before).
 *   - flag OFF → do NOT block; show a non-blocking migration warning at most. This is the
 *     deploy-dark default so legacy {{unsubscribe}} templates keep working until the business
 *     migrates them to ASM tags and flips the flag.
 *
 * A read that is still loading OR fails is treated as OFF for the UI: the backend is the real,
 * fail-closed guard, so a transient client read glitch must never LOCK OUT operators — the worst
 * case is the backend refuses the send, never a false block in the form. (This is the deliberate
 * opposite of the backend's fail-closed-on-error, and is safe precisely because the backend is
 * authoritative.)
 *
 * The flag is rarely flipped (once at activation, maybe once at rollback). We use React Query with
 * window-focus + periodic refetch so an ALREADY-OPEN session converges to a flag flip WITHOUT a
 * manual reload (audit FG MED-3: a one-shot useEffect read left open tabs stale until reload). The
 * refetch is cheap (one indexed system_config row).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../services/supabase";

// How often an open session re-checks the flag. Short enough that an activation/rollback flip
// propagates to open tabs within a minute or so; the query is a single-row indexed read.
const REFETCH_MS = 60_000;

export function useTemplateGuardEnabled(): boolean {
  const { data } = useQuery<boolean>({
    queryKey: ["asm-template-guard-enabled"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", "asm_template_guard_enabled")
        .maybeSingle();
      // Only the exact string "true" enables the gates; missing row / error / any other value
      // → OFF (do not block the operator; defer to the fail-closed backend).
      return !error && data?.value === "true";
    },
    // Converge open sessions on a flag flip without a manual reload.
    refetchInterval: REFETCH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  return data ?? false;
}
